import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { PopupScratchContent } from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import { fontStack, textCss, mobileSizeProps } from "./engine";

interface ScratchStageProps {
  scratch: PopupScratchContent;
  /** The welcome step's resolved design (+ steps.scratch overrides) — the
   *  scratch page wears the same skin so scratch → gate reads as one flow. */
  step: ResolvedStepDesign;
  /** Light panel = dark chrome accents; dark panel = light. */
  panelIsLight: boolean;
  /** CTA (Claim) clicked after the reveal — advance to the form. */
  onAdvance: () => void;
  /** Fires exactly once, the moment the cover crosses the reveal threshold. */
  onReveal?: () => void;
  /** Editor static preview: cover renders but never scratches or advances. */
  inert?: boolean;
}

/** Fraction of the cover that must be cleared before the card auto-reveals.
 *  0.4 = the industry default (Alia ships the same number). */
const DEFAULT_PCT = 0.4;
/** Brush radius in buffer px. */
const DEFAULT_BRUSH = 24;
/** Cover fade-out length once the threshold is crossed. */
const REVEAL_MS = 450;

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Fraction of the canvas already scratched away. Counts fully-transparent
 * pixels (alpha === 0 — exactly what destination-out leaves behind) over a
 * sampled grid; sampleStep=4 reads every 4th pixel, which is indistinguishable
 * from a full count at threshold precision and 4x cheaper.
 */
export function clearedFraction(
  data: Uint8ClampedArray,
  sampleStep = 4
): number {
  const stride = 4 * Math.max(1, sampleStep);
  let cleared = 0;
  let sampled = 0;
  for (let i = 3; i < data.length; i += stride) {
    sampled++;
    if (data[i] === 0) cleared++;
  }
  return sampled === 0 ? 0 : cleared / sampled;
}

/** Procedural gold-foil cover: gradient base, diagonal sheen bands, flecks,
 *  and the label text — so the default scratch card needs no asset at all. */
function paintGoldCover(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  label: string
) {
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#e8cc6d");
  g.addColorStop(0.28, "#d9b64e");
  g.addColorStop(0.5, "#f3e08e");
  g.addColorStop(0.72, "#d3ac41");
  g.addColorStop(1, "#e6c862");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // Diagonal sheen bands
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-0.35);
  for (let i = -3; i <= 3; i++) {
    ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.10)" : "rgba(120,90,20,0.06)";
    ctx.fillRect(-w, i * (h / 4), w * 2, h / 9);
  }
  ctx.restore();

  // Deterministic flecks (no Math.random — repaints must be identical)
  for (let i = 0; i < 90; i++) {
    const x = ((i * 137.508) % 1) * w + ((i * 61) % 13);
    const y = (((i * 97.31) % 1) * h + ((i * 29) % 11)) % h;
    ctx.fillStyle = i % 3 ? "rgba(255,255,255,0.12)" : "rgba(110,80,15,0.10)";
    ctx.fillRect(x % w, y, 2, 2);
  }

  if (label) {
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = `700 ${Math.max(16, Math.round(h / 8))}px Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(120,90,20,0.45)";
    ctx.shadowBlur = 6;
    // Manual letter-spacing: canvas has no reliable letterSpacing API
    const spaced = label.toUpperCase().split("").join("  ");
    ctx.fillText(spaced, w / 2, h / 2, w - 24);
    ctx.shadowBlur = 0;
  }
}

/**
 * The scratch-card game step (the Alia/Retropia pattern). A prize sits under
 * a foil cover drawn on a canvas; the visitor scratches with pointer drags
 * (destination-out brush). When enough of the cover is gone the rest fades
 * out, the prize stands revealed and the CTA appears. Keyboard/AT users can
 * press Enter to reveal instantly — a scratch gesture is never the only way
 * through. Nothing is persisted here: the CTA advances to the form step.
 */
export const ScratchStage: React.FC<ScratchStageProps> = ({
  scratch,
  step,
  panelIsLight,
  onAdvance,
  onReveal,
  inert = false,
}) => {
  const [revealed, setRevealed] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arenaRef = useRef<HTMLDivElement>(null);
  // Buffer size in CSS px (before DPR scaling) — frozen at mount so scratch
  // progress survives viewport resizes; pointer coords map through the rect.
  const bufRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const checkingRef = useRef(false);
  const revealedRef = useRef(false);

  const pct = Math.min(Math.max(scratch.pct_to_reveal ?? DEFAULT_PCT, 0.05), 0.95);
  const brush = Math.max(8, scratch.brush_size ?? DEFAULT_BRUSH);

  const headingEl = step.elements.heading;
  const subEl = step.elements.subheading;
  const headingColor = headingEl.color || "#ffffff";
  const subColor = subEl.color || headingColor;
  // The page wears the design's heading/subheading typography exactly like
  // the form step does (size presets or exact px, weight, italic, family,
  // line-height, letter-spacing) plus the exact phone sizes — so the game
  // page and the form read as one design, and phone type can be larger
  // than desktop the way the layouts this recreates are.
  const headingText = textCss("heading", { ...headingEl, color: headingColor });
  const subText = textCss("subheading", { ...subEl, color: subColor });
  const headingMobile = mobileSizeProps(headingEl);
  const subMobile = mobileSizeProps(subEl);
  const headingFont = headingEl.fontFamily
    ? fontStack(headingEl.fontFamily)
    : undefined;
  const prizeTextFont = step.elements.body?.fontFamily
    ? fontStack(step.elements.body.fontFamily)
    : undefined;
  const prizeSize = scratch.prize_size_px && scratch.prize_size_px > 0 ? scratch.prize_size_px : 30;
  const prizeMobile = mobileSizeProps({ mobileSizePx: scratch.prize_mobile_size_px });
  const prizeBg =
    scratch.prize_bg || (panelIsLight ? "#f7f4ec" : "#141a2e");
  const prizeTransparent = prizeBg.trim().toLowerCase() === "transparent";
  const prizeColor =
    scratch.prize_text_color || (panelIsLight ? "#111827" : "#ffffff");
  const btn = step.elements.button;

  // ---- cover painting ----
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const arena = arenaRef.current;
    if (!canvas || !arena) return;
    const rect = arena.getBoundingClientRect();
    // jsdom (tests) measures 0x0 — fall back to the configured size so the
    // component still exercises its logic.
    const w = Math.round(rect.width) || scratch.card_width || 340;
    const h = Math.round(rect.height) || scratch.card_height || 230;
    bufRef.current = { w, h };
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const paint = () => {
      ctx.globalCompositeOperation = "source-over";
      if (scratch.cover_image_url) {
        // Gold base shows while the image loads (and if it never does)
        ctx.fillStyle = "#d9b64e";
        ctx.fillRect(0, 0, w, h);
      } else {
        paintGoldCover(ctx, w, h, scratch.cover_label ?? "Scratch here");
      }
    };
    paint();

    if (scratch.cover_image_url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = scratch.cover_image_url;
      img.onload = () => {
        // object-fit: cover mapping into the buffer
        const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      };
      return () => {
        img.onload = null;
      };
    }
  }, [
    scratch.cover_image_url,
    scratch.cover_label,
    scratch.card_width,
    scratch.card_height,
  ]);

  // ---- reveal ----
  const reveal = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    const canvas = canvasRef.current;
    if (canvas) {
      if (!reducedMotion()) {
        canvas.style.transition = `opacity ${REVEAL_MS}ms ease`;
      }
      canvas.style.opacity = "0";
      canvas.style.pointerEvents = "none";
    }
    setRevealed(true);
    onReveal?.();
  }, [onReveal]);

  // rAF-throttled threshold check — getImageData is the expensive call, so at
  // most one runs per frame while scratching.
  const checkThreshold = useCallback(() => {
    if (checkingRef.current || revealedRef.current) return;
    checkingRef.current = true;
    requestAnimationFrame(() => {
      checkingRef.current = false;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d", { alpha: true });
      if (!canvas || !ctx) return;
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        if (clearedFraction(data) >= pct) reveal();
      } catch {
        // A tainted canvas (cross-origin cover without CORS) throws here.
        // The visitor can still get through: their next tap after a good
        // scratch attempt, or Enter, reveals. Never trap them.
      }
    });
  }, [pct, reveal]);

  // ---- scratching ----
  const toBuffer = useCallback((e: { clientX: number; clientY: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const { w, h } = bufRef.current;
    return {
      x: ((e.clientX - rect.left) / Math.max(1, rect.width)) * w,
      y: ((e.clientY - rect.top) / Math.max(1, rect.height)) * h,
    };
  }, []);

  const dab = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number) => {
      ctx.beginPath();
      ctx.arc(x, y, brush, 0, Math.PI * 2);
      ctx.fill();
    },
    [brush]
  );

  const stroke = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number) => {
      const last = lastRef.current;
      if (last) {
        // Interpolate circles along the segment so fast drags leave no gaps
        const dist = Math.hypot(x - last.x, y - last.y);
        const gap = Math.max(1, brush * 0.4);
        const n = Math.ceil(dist / gap);
        for (let i = 1; i <= n; i++) {
          dab(ctx, last.x + ((x - last.x) * i) / n, last.y + ((y - last.y) * i) / n);
        }
      } else {
        dab(ctx, x, y);
      }
      lastRef.current = { x, y };
    },
    [brush, dab]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (inert || revealedRef.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d", { alpha: true });
      if (!canvas || !ctx) return;
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* best-effort */
      }
      drawingRef.current = true;
      lastRef.current = null;
      ctx.globalCompositeOperation = "destination-out";
      const { x, y } = toBuffer(e);
      stroke(ctx, x, y);
      checkThreshold();
    },
    [inert, toBuffer, stroke, checkThreshold]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const ctx = canvasRef.current?.getContext("2d", { alpha: true });
      if (!ctx) return;
      // Coalesced events keep fast flicks continuous on 120Hz+ inputs
      const native = e.nativeEvent as PointerEvent;
      const events =
        typeof native.getCoalescedEvents === "function"
          ? native.getCoalescedEvents()
          : [native];
      for (const ev of events.length ? events : [native]) {
        const { x, y } = toBuffer(ev);
        stroke(ctx, x, y);
      }
      checkThreshold();
    },
    [toBuffer, stroke, checkThreshold]
  );

  const onPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false;
      lastRef.current = null;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d", { alpha: true });
      if (ctx) ctx.globalCompositeOperation = "source-over";
      try {
        canvas?.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    },
    []
  );

  // Keyboard / AT path: Enter reveals instantly.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (inert) return;
      if (e.key === "Enter" && !revealedRef.current) {
        e.preventDefault();
        reveal();
      }
    },
    [inert, reveal]
  );

  // A disabled or prize-less scratch page never renders — the widget's
  // playability gate mirrors this, so both agree.
  if (!scratch.enabled || !(scratch.prize_text || "").trim()) return null;

  const cardW = scratch.card_width || 340;
  const cardH = scratch.card_height || 230;

  return (
    <div
      className="flowapt-scratch"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        gap: "clamp(14px, 2.2vh, 22px)",
        padding: "clamp(22px, 4vh, 40px) clamp(16px, 3vw, 28px)",
        fontFamily: fontStack(step.theme.fontFamily),
      }}
    >
      {scratch.logo_url && (
        <img
          src={scratch.logo_url}
          alt=""
          draggable={false}
          className={scratch.logo_mobile_width ? "fp-w-m" : undefined}
          style={{
            width: Math.max(24, scratch.logo_width ?? 80),
            ...(scratch.logo_mobile_width
              ? { ["--fp-w-m" as string]: `${Math.max(24, scratch.logo_mobile_width)}px` }
              : {}),
            maxWidth: "60vw",
            height: "auto",
            marginBottom: 4,
          }}
        />
      )}
      {scratch.heading && (
        <p
          className={headingMobile.className}
          style={{
            margin: 0,
            ...headingText,
            ...headingMobile.style,
            textAlign: "center",
            maxWidth: "24ch",
          }}
        >
          {scratch.heading}
        </p>
      )}
      {scratch.sub_heading && (
        <p
          className={subMobile.className}
          style={{
            margin: 0,
            ...subText,
            ...subMobile.style,
            textAlign: "center",
            maxWidth: "40ch",
          }}
        >
          {scratch.sub_heading}
        </p>
      )}

      {/* The card: prize underneath, scratchable cover canvas on top */}
      <div
        ref={arenaRef}
        role="button"
        tabIndex={inert ? -1 : 0}
        aria-label={
          revealed
            ? `Your reward: ${scratch.prize_text}`
            : "Scratch the card to reveal your reward. Press Enter to reveal it immediately."
        }
        onKeyDown={onKeyDown}
        style={{
          position: "relative",
          width: `min(86vw, ${cardW}px)`,
          height: `min(46vh, ${cardH}px)`,
          borderRadius: 16,
          overflow: "hidden",
          // A transparent prize reveals straight onto the page: no shadow
          // either, or the invisible card would cast a visible halo.
          boxShadow: prizeTransparent ? "none" : "0 14px 40px -14px rgba(0,0,0,0.55)",
          outline: "none",
        }}
      >
        {/* Prize layer */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: prizeBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "16px 20px",
            textAlign: "center",
          }}
        >
          {scratch.prize_heading && (
            <span
              className={subMobile.className}
              style={{
                ...subText,
                color: prizeColor,
                fontStyle: "italic",
                fontFamily: headingFont,
                ...subMobile.style,
                opacity: 0.9,
              }}
            >
              {scratch.prize_heading}
            </span>
          )}
          <span
            className={prizeMobile.className}
            style={{
              color: prizeColor,
              fontWeight: 800,
              fontFamily: prizeTextFont,
              fontSize: `clamp(${Math.max(14, Math.round(prizeSize * 0.66))}px, ${(prizeSize / 7.4).toFixed(2)}vw, ${prizeSize}px)`,
              lineHeight: 1.2,
              ...prizeMobile.style,
            }}
          >
            {scratch.prize_text}
          </span>
        </div>

        {/* Scratchable cover */}
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          data-testid="scratch-cover"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            touchAction: "none",
            cursor: inert
              ? "default"
              : revealed
                ? "default"
                : scratch.cursor_image_url
                  ? `url("${scratch.cursor_image_url}") 20 20, grab`
                  : "grab",
          }}
        />
      </div>

      {/* CTA — reserved space from the start (visibility, not conditional
          mount) so the reveal never reflows the page under the card. */}
      <button
        type="button"
        onClick={() => {
          if (!inert && revealed) onAdvance();
        }}
        tabIndex={revealed && !inert ? 0 : -1}
        aria-hidden={!revealed}
        className={
          [revealed ? "flowapt-animated" : "", btn.mobileFontSizePx > 0 ? "fp-fs-m" : ""]
            .filter(Boolean)
            .join(" ") || undefined
        }
        style={{
          visibility: revealed ? "visible" : "hidden",
          marginTop: 4,
          // Same footprint as the card; height + label size follow the
          // design's button element (phone values under 768px).
          width: `min(86vw, ${cardW}px)`,
          height: btn.customHeightPx > 0 ? btn.customHeightPx : undefined,
          padding: btn.customHeightPx > 0 ? "0 24px" : "14px 34px",
          borderRadius: btn.radius || "10px",
          border: "none",
          background: btn.background || "#ffffff",
          color: btn.textColor || "#111827",
          fontWeight: 700,
          fontSize: btn.fontSizePx > 0 ? btn.fontSizePx : 16,
          ...(btn.mobileFontSizePx > 0
            ? { ["--fp-fs-m" as string]: `${btn.mobileFontSizePx}px` }
            : {}),
          ...(btn.customMobileHeightPx > 0
            ? { ["--fp-btn-h-m" as string]: `${btn.customMobileHeightPx}px` }
            : {}),
          cursor: revealed && !inert ? "pointer" : "default",
          fontFamily: "inherit",
          boxShadow: prizeTransparent ? "none" : "0 8px 24px -10px rgba(0,0,0,0.4)",
        }}
      >
        {scratch.button_text || "Claim"}
      </button>
    </div>
  );
};

export default ScratchStage;
